'use client'

import { cn } from '@brew/ui/cn'
import { Check, Minus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  type FeatureComparisonSection,
  FeatureComparisonTable,
} from '@/components/marketing/feature-comparison-table'
import type { FeatureComparison } from '@/lib/site/seo/comparison-content'

interface ComparisonTableProps {
  features: ReadonlyArray<FeatureComparison>
  brewName?: string
  competitorName: string
}

function FeatureIcon({ value }: { value: 'yes' | 'limited' | 'no' | string }) {
  if (value === 'yes') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border">
        <Check className="size-3.5 text-foreground" />
      </span>
    )
  }
  if (value === 'no') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center text-muted-foreground/50">
        <span className="text-lg leading-none">–</span>
      </span>
    )
  }
  if (value === 'limited') {
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border">
        <Minus className="size-3.5 text-muted-foreground" />
      </span>
    )
  }
  return <span className="text-muted-foreground text-sm">{value}</span>
}

function FeatureValue({ value, note }: { value: string; note?: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <FeatureIcon value={value} />
      {note ? (
        <span className="max-w-[150px] text-center text-muted-foreground text-xs">
          {note}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Brew vs one competitor, grouped by category — the `/compare/<competitor>`
 * body, built on the shared `FeatureComparisonTable`.
 */
export function ComparisonTable({
  features,
  brewName = 'Brew',
  competitorName,
}: ComparisonTableProps) {
  const t = useTranslations('comparePage')
  const categories = features.reduce(
    (acc, feature) => {
      if (!acc[feature.category]) {
        acc[feature.category] = []
      }
      acc[feature.category].push(feature)
      return acc
    },
    {} as Record<string, Array<FeatureComparison>>
  )

  const sections: Array<FeatureComparisonSection> = Object.entries(
    categories
  ).map(([category, categoryFeatures]) => ({
    key: `category-${category}`,
    heading: {
      rowClassName: 'bg-muted/30',
      className: 'marketing-eyebrow px-4 py-2',
      children: category,
    },
    rows: categoryFeatures.map((feature, index) => ({
      key: feature.feature,
      className: cn(
        'border-border border-b transition-colors hover:bg-muted/20',
        index === categoryFeatures.length - 1 && 'border-b-0'
      ),
      cells: [
        {
          className: 'px-4 py-4',
          children: (
            <span className="font-medium text-foreground text-sm md:text-base">
              {feature.feature}
            </span>
          ),
        },
        {
          className: 'px-4 py-4',
          children: (
            <FeatureValue value={feature.brew} note={feature.brewNote} />
          ),
        },
        {
          className: 'px-4 py-4',
          children: (
            <FeatureValue
              value={feature.competitor}
              note={feature.competitorNote}
            />
          ),
        },
      ],
    })),
  }))

  return (
    <div className="w-full overflow-x-auto">
      <FeatureComparisonTable
        headRowClassName="border-border border-b"
        columns={[
          {
            key: 'feature',
            className:
              'w-1/2 px-4 py-4 text-left font-medium text-foreground text-sm',
            children: t('features.featureColumn'),
          },
          {
            key: 'brew',
            className:
              'w-1/4 px-4 py-4 text-center font-medium text-foreground text-sm',
            children: brewName,
          },
          {
            key: 'competitor',
            className:
              'w-1/4 px-4 py-4 text-center font-medium text-foreground text-sm',
            children: competitorName,
          },
        ]}
        sections={sections}
      />
    </div>
  )
}
